import React from "react";
import type { Theme } from "@material-ui/core/styles";
import { createStyles, makeStyles, useTheme } from "@material-ui/core/styles";
import type { IImageData, IAlarmZone } from "App/Modules/Models";
import { ImageList, ImageListItem, ImageListItemBar, useMediaQuery, Box } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faImage } from "@fortawesome/pro-light-svg-icons";
import { formatDateTime } from "../../Utils";
import { AspectRatio } from "../AspectRatio/AspectRatio";

type Props = {
    images: { image: IImageData; alarmZones: IAlarmZone[] }[];
    timeZone: string;
    showAlarmZones: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-around",
        },
        gridList: {
            // promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
            transform: "translateZ(0)",
            width: "100%",
        },
        titleBar: {
            background: `linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)`,
        },
        alarmzoneImg: {
            position: "absolute",
            pointerEvents: "none",
            top: 0,
            left: 0,
            zIndex: 1,
        },
        img: {
            width: "100%",
            height: "100%",
        },
        noDataMessage: {
            ...theme.typography.h1,
            color: theme.palette.text.secondary,
            "& .svg-inline--fa": {
                color: theme.palette.text.disabled,
            },
            textAlign: "center",
        },
    }),
);

export default function ImageGridList(props: Props) {
    const theme = useTheme();
    const classes = useStyles();

    const { t } = useTranslation();

    const xsDown = useMediaQuery(theme.breakpoints.down("xs"));
    const smToMd = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const lgUp = useMediaQuery(theme.breakpoints.up("lg"));

    const getImageListCols = () => {
        let cols = 0;

        if (xsDown) {
            cols = 1;
        }

        if (smToMd) {
            cols = 2;
        }

        if (lgUp) {
            cols = 3;
        }

        return cols;
    };

    return props.images.length > 0 ? (
        <div className={classes.root}>
            <ImageList rowHeight="auto" className={classes.gridList} cols={getImageListCols()} gap={10}>
                {props.images.map(({ image, alarmZones }) => (
                    <ImageListItem key={image.url}>
                        <AspectRatio ratio={image.width / image.height}>
                            <React.Fragment>
                                {props.showAlarmZones ? (
                                    <svg
                                        viewBox={`0 0 ${image.width} ${image.height}`}
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={classes.alarmzoneImg}
                                    >
                                        {alarmZones.map((alarmZone, alarmZoneIndex) => {
                                            const { hexColor } = alarmZone.configurationGroup;
                                            const coords = alarmZone.polygon.map((polygon, polyIndex) => {
                                                const points = `${polygon.x * image.width},${polygon.y * image.height}`;
                                                return polyIndex === 0 ? `M${points}` : `L${points}`;
                                            });
                                            return (
                                                <g
                                                    key={`${alarmZone.id}-${alarmZoneIndex}`}
                                                    fill={`${hexColor}`}
                                                    fillOpacity="0.3"
                                                    stroke={`${hexColor}`}
                                                    strokeOpacity="0.7"
                                                    strokeWidth={1}
                                                >
                                                    <path
                                                        d={`${coords.join(" ")} Z`}
                                                        vectorEffect="non-scaling-stroke"
                                                    />
                                                </g>
                                            );
                                        })}
                                    </svg>
                                ) : null}
                                <img className={classes.img} src={image.url} alt={image.altText} />
                            </React.Fragment>
                        </AspectRatio>
                        <ImageListItemBar
                            title={image.altText}
                            subtitle={
                                image.dtImage
                                    ? formatDateTime(image.dtImage, props.timeZone)
                                    : t("Gui.Common.NoImageUploaded")
                            }
                            position="top"
                            className={classes.titleBar}
                        />
                    </ImageListItem>
                ))}
            </ImageList>
        </div>
    ) : (
        <Box pt={10} pb={10} className={classes.noDataMessage}>
            <FontAwesomeIcon icon={faImage} /> {t("Gui.Common.NoImagesAvailable")}
        </Box>
    );
}
